import { and, asc, eq, isNotNull, ne, sql } from "drizzle-orm";

import type { WsFeedGroupMembership } from "@storyboard/web-client";
import { bulkInsert } from "@/db/bulkInsert";
import { db } from "@/db/db";
import { feed, feedGroup, feedGroupMembership, feedPreference, permission } from "@/db/schema";
import { deduplicateByKeyValue } from "@/models/appSyncFunctions.ts";
import { emptyQuery } from "@/models/emptyQuery.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";

function transformWsFeedGroupMembership(r: WsFeedGroupMembership) {
  return {
    id: r.id,
    createdAt: r.createdAt ?? null,
    updatedAt: r.updatedAt ?? r.createdAt ?? null,
    groupId: r.groupId,
    feedId: r.feedId,
    workspaceId: r.workspaceId,
    enabled: r.enabled ? 1 : 0,
  };
}

export async function bulkUpsertFeedGroupMembership(records: WsFeedGroupMembership[]) {
  if (records.length === 0) return;
  const data = records.map((r) => transformWsFeedGroupMembership(r));
  const deduped = deduplicateByKeyValue(data, (item) => item.feedId);
  await bulkInsert(feedGroupMembership, deduped, {
    onConflict: {
      type: "doUpdate",
      target: [feedGroupMembership.workspaceId, feedGroupMembership.feedId],
      set: {
        id: sql`"excluded"."id"`,
        updatedAt: sql`"excluded"."updatedAt"`,
        groupId: sql`"excluded"."groupId"`,
        enabled: sql`"excluded"."enabled"`,
      },
    },
  });
  emitTableUpdates(["feed_group_membership"]);
}

export const fetchSubscriptionStatus = async ({ workspaceMembershipId }) => {
  const select = {
    feedId: feed.id,
    pushNotificationPreference: feedPreference.pushNotificationPreference,
    groupId: feedGroupMembership.groupId,
    subscribed: sql`${feedPreference.pushNotificationPreference} != 'none' as "subscribed"`,
    title: feed.title,
  };
  return db
    .select(select)
    .from(feedPreference)
    .innerJoin(feed, eq(feed.id, feedPreference.feedId))
    .leftJoin(feedGroupMembership, eq(feed.id, feedGroupMembership.feedId))
    .innerJoin(
      permission,
      and(
        eq(feed.id, permission.feedId),
        eq(permission.workspaceMembershipId, workspaceMembershipId),
        eq(permission.name, "read"),
      ),
    )
    .where(eq(feedPreference.workspaceMembershipId, workspaceMembershipId));
};
export const fetchAllGroupsWithSubscribedStatusQuery = async ({
  workspaceMembershipId,
  groupId,
}: {
  workspaceMembershipId: string;
  groupId: string;
}) => {
  return db
    .select({
      id: feedGroupMembership.id,
      groupId: feedGroupMembership.groupId,
      subscribed: sql`${feedPreference.pushNotificationPreference} != 'none' as "subscribed"`,
      feedId: feedGroupMembership.feedId,
      title: feed.title,
      pushNotificationPreference: feedPreference.pushNotificationPreference,
    })
    .from(feedGroupMembership)
    .innerJoin(feed, eq(feed.id, feedGroupMembership.feedId))
    .innerJoin(
      permission,
      and(
        eq(feed.id, permission.feedId),
        eq(permission.workspaceMembershipId, workspaceMembershipId),
        eq(permission.name, "read"),
      ),
    )
    .leftJoin(
      feedPreference,
      and(eq(feed.id, feedPreference.feedId), eq(feedPreference.workspaceMembershipId, workspaceMembershipId)),
    )
    .where(eq(feedGroupMembership.groupId, groupId))
    .groupBy(
      feedPreference.pushNotificationPreference,
      feedGroupMembership.id,
      feedGroupMembership.groupId,
      feedGroupMembership.feedId,
      feed.title,
      sql`"subscribed"`,
    );
};
export const myWorkspaceFeedGroups = (workspaceId?: string, workspaceMembershipId?: string) => {
  const query = db
    .select({
      id: feedGroup.id,
      groupId: feedGroupMembership.groupId,
      name: feedGroup.name,
    })
    .from(feedGroupMembership)
    .innerJoin(feedGroup, eq(feedGroupMembership.groupId, feedGroup.id))
    .innerJoin(
      permission,
      and(
        eq(feedGroupMembership.feedId, permission.feedId),
        eq(permission.name, "read"),
        eq(permission.enabled, true),
        eq(permission.workspaceMembershipId, workspaceMembershipId),
      ),
    )
    .where(
      and(
        isNotNull(feedGroup.name),
        ne(feedGroup.name, ""),
        eq(feedGroup.workspaceId, workspaceId),
        eq(feedGroup.enabled, 1),
      ),
    )
    .groupBy(feedGroupMembership.groupId, feedGroup.name, feedGroup.id)
    .orderBy(asc(feedGroup.name));

  if (!workspaceId || !workspaceMembershipId) {
    return emptyQuery as unknown as typeof query;
  }
  return query;
};
